/**
 * Cycle setup phase — determines whether a newly created cycle should use the
 * guided setup wizard vs. the full CycleDetail admin surface.
 *
 * Setup completes when the creator kicks off Catchball (or finishes a non-Catchball
 * cycle launch). Persisted on cycle.metadata.setupKickoffAt.
 */

import type { DegapCycle } from '../types/prioritization/degapCycle';
import type { CatchballLoop } from '../types/prioritization/catchballLoop';

export const SETUP_KICKOFF_AT_KEY = 'setupKickoffAt';
/** OST linked during setup step 1 — pre-seeds kickoff composer. */
export const SETUP_OST_TREE_ID_KEY = 'setupOstTreeId';
export const SETUP_OST_TREE_NAME_KEY = 'setupOstTreeName';

export function getSetupOstTreeId(cycle: DegapCycle | null | undefined): string | null {
  if (!cycle?.metadata || typeof cycle.metadata !== 'object') return null;
  const value = (cycle.metadata as Record<string, unknown>)[SETUP_OST_TREE_ID_KEY];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export type CycleSetupStepId = 'configure' | 'catchball' | 'kickoff';

export function getSetupKickoffAt(cycle: DegapCycle | null | undefined): string | null {
  if (!cycle?.metadata || typeof cycle.metadata !== 'object') return null;
  const value = (cycle.metadata as Record<string, unknown>)[SETUP_KICKOFF_AT_KEY];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** True when a loop has left drafting — request was sent (legacy cycles without metadata). */
export function hasCatchballKickoff(loops: CatchballLoop[]): boolean {
  return loops.some((loop) => loop.state !== 'drafting' && loop.state !== 'closed');
}

export function isCycleSetupComplete(
  cycle: DegapCycle | null | undefined,
  loops: CatchballLoop[] = [],
): boolean {
  if (!cycle) return false;
  if (getSetupKickoffAt(cycle)) return true;
  return hasCatchballKickoff(loops);
}

export interface ShouldUseSetupWizardInput {
  cycle: DegapCycle | null | undefined;
  loops?: CatchballLoop[];
  userId: string | null | undefined;
  isCycleOwner: boolean;
  isAdmin: boolean;
  /** When true, caller is explicitly opening advanced CycleDetail (?advanced=1). */
  advancedMode?: boolean;
}

export function shouldUseSetupWizard(input: ShouldUseSetupWizardInput): boolean {
  const { cycle, loops = [], userId, isCycleOwner, isAdmin, advancedMode } = input;
  if (!cycle || !userId) return false;
  if (advancedMode) return false;
  if (!isCycleOwner && !isAdmin) return false;
  return !isCycleSetupComplete(cycle, loops);
}

export function getSetupSteps(catchballEnabled: boolean): CycleSetupStepId[] {
  if (catchballEnabled) {
    return ['configure', 'catchball', 'kickoff'];
  }
  return ['configure', 'kickoff'];
}

export function parseSetupStepParam(
  raw: string | null,
  catchballEnabled: boolean,
): CycleSetupStepId {
  const steps = getSetupSteps(catchballEnabled);
  if (raw === '1' || raw === 'configure') return 'configure';
  if (catchballEnabled) {
    if (raw === '2' || raw === 'catchball') return 'catchball';
    if (raw === '3' || raw === 'kickoff') return 'kickoff';
  } else if (raw === '2' || raw === 'kickoff') {
    return 'kickoff';
  }
  return steps[0];
}

export function setupStepIndex(step: CycleSetupStepId, catchballEnabled: boolean): number {
  const steps = getSetupSteps(catchballEnabled);
  const idx = steps.indexOf(step);
  return idx >= 0 ? idx : 0;
}

export function setupStepNumber(step: CycleSetupStepId, catchballEnabled: boolean): number {
  return setupStepIndex(step, catchballEnabled) + 1;
}

export function mergeSetupKickoffMetadata(
  cycle: DegapCycle,
  kickoffAt: string = new Date().toISOString(),
): Record<string, unknown> {
  const existing =
    cycle.metadata && typeof cycle.metadata === 'object'
      ? { ...(cycle.metadata as Record<string, unknown>) }
      : {};
  return { ...existing, [SETUP_KICKOFF_AT_KEY]: kickoffAt };
}
